import { Customer } from '@/types/Customer';
import { buildAppContext } from 'client-utils/analyticsContext';
import { includes } from 'lodash';
import TagManager from 'react-gtm-module';
import { ThumbsSurveyData } from 'ui';
import {
  getCustomerAccount,
  userIsCustomer,
  userIsIndividual,
} from './customerUtils';

declare const window: Window & { dataLayer: Record<string, unknown>[] };

type UserType =
  'Private, XELLENT' | 'Private, ZYNERGY' | 'Company' | 'NonCustomer';
const appName = 'mitt_me';

const testOrProduction =
  includes(window.location.href, 'mitt.malarenergi') ||
  includes(window.location.href, 'mitt-test.malarenergi');
const production = includes(window.location.href, 'mitt.malarenergi');
export const getCustomerType = (customer: Customer) => {
  if (userIsIndividual(customer)) {
    const customerAccount = getCustomerAccount(customer);
    return `Private, ${customerAccount.sourceSystem?.name || ''}` as UserType;
  }
  return 'Company' as UserType;
};

export const trackingInit = (customer?: Customer | null) => {
  if (!testOrProduction) {
    return;
  }
  // Must be in dataLayer before gtm.js loads so GTM can read it on init
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(
    buildAppContext({
      appName,
      productionHost: 'mitt.malarenergi.se',
      hostname: window.location.hostname,
      version: import.meta.env.VITE_APP_VERSION,
    }),
  );
  if (customer) {
    const userType = userIsCustomer(customer)
      ? getCustomerType(customer)
      : 'NonCustomer';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'userParameters',
      userType,
    });
  }

  const tagManagerArgs = {
    gtmId: 'GTM-KCH4HXV',
  };
  TagManager.initialize(tagManagerArgs);
};

export const trackingLogin = (userType: UserType, customer?: Customer) => {
  if (!testOrProduction) {
    return;
  }
  setTimeout(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'Login',
        step: 'login',
        application: appName,
        userType,
        tjänster: customer?.utilities?.map((u) => u.name).join(','),
      },
    });
  }, 5000);
};

export const trackingSwitchCustomer = (customer: Customer) => {
  if (!testOrProduction) {
    return;
  }
  const userType = userIsCustomer(customer)
    ? getCustomerType(customer)
    : 'NonCustomer';
  TagManager.dataLayer({
    dataLayer: {
      event: 'Login',
      step: 'change',
      application: appName,
      tjänster: customer?.utilities?.map((u) => u.name).join(','),
      userType,
    },
  });
};

export const trackingSimpleEvent = (event: string) => {
  if (!testOrProduction) {
    return;
  }
  TagManager.dataLayer({
    dataLayer: {
      event,
      application: appName,
    },
  });
};

export const trackingDataEvent = (event: string, data: any) => {
  if (!production) {
    console.log('trackingDataEvent', event, data);
  }
  if (!testOrProduction) {
    return;
  }
  TagManager.dataLayer({
    dataLayer: {
      event,
      event_info: data,
      application: appName,
    },
  });
};

export const trackingSubmitDrainReading = () => {
  trackingSimpleEvent('skicka mätarställning');
};

export const trackingThumbsSurvey = (data: ThumbsSurveyData) => {
  if (!testOrProduction) {
    return;
  }
  if (!production) {
    console.debug('trackingThumbsSurvey', {
      event: 'feedback',
      step: data.thumbs === 'thumbs-up' ? 'positiv' : 'negativ',
      event_info: data.optionalText,
      page_variant: data.page_variant || 'unknown',
    });
  }
  TagManager.dataLayer({
    dataLayer: {
      event: 'feedback',
      step: data.thumbs === 'thumbs-up' ? 'positiv' : 'negativ',
      event_info: data.optionalText,
      page_variant: data.page_variant || 'unknown',
    },
  });
};
